import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import { H3 } from 'UIComponents/elements/headings';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getAllFeaturesByAllProductLinesSelector } from '../../../selectors/FeatureSelectors';
import SuiteFeatureTableStickyHeader from './SuiteFeatureTableStickyHeader';
import { Section } from '../../common/SharedComponents';
import { SuiteFeatureTableContent } from './SuiteFeatureTableContent';
import { CommerceHubFeatureContent } from './CommerceHubFeatureContent';
import { Table, TableContainer } from './SuiteFeatureTableComponents';
import { AllCombinedProductLines } from 'self-service-api/constants/AllCombinedProductLines';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
const initialSectionState = AllCombinedProductLines.reduce((agg, curr) => {
  agg[curr] = true;
  return agg;
}, {});
const initialFeatureTableState = AllCombinedProductLines.reduce((agg, curr) => {
  agg[curr] = false;
  return agg;
}, {});

const SuiteFeatureTable = ({
  productsByProductLine,
  screen,
  tab,
  isFreeToStarter,
  trackingSource,
  titleKey,
  isLearnMoreModal = false
}) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(initialSectionState);
  const [isFeatureTableExpanded, setIsFeatureTableExpanded] = useState(initialFeatureTableState);
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);

  const handleExpandSectionChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isSectionExpanded[productLine] ? 'collapse' : 'expand'}-section`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsSectionExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  const handleExpandFeatureTableChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isFeatureTableExpanded[productLine] ? 'collapse' : 'expand'}-table`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsFeatureTableExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };

  if (!Object.keys(features).length) return null;
  const sortedProductLines = sortProductLines(Object.keys(features));
  const levels = new Set();
  Object.values(productsByProductLine).forEach(products => {
    products.forEach(product => {
      const level = getProductLevelFromApiNameString(product);
      levels.add(level);
    });
  });
  return /*#__PURE__*/_jsx(Section, {
    children: /*#__PURE__*/_jsxs(TableContainer, {
      ref: featureTableRef,
      columns: levels.size,
      "data-selenium": "suite-feature-table",
      id: "feature-table",
      children: [/*#__PURE__*/_jsx("div", {
        className: "text-center m-y-5",
        children: /*#__PURE__*/_jsx(H3, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: titleKey || 'suiteFeatureTable.title'
          })
        })
      }), /*#__PURE__*/_jsx(SuiteFeatureTableStickyHeader, {
        levels: [...levels],
        tableRef: featureTableRef,
        isFreeToStarter: isFreeToStarter,
        isLearnMoreModal: isLearnMoreModal,
        screen: screen
      }), /*#__PURE__*/_jsxs(Table, {
        children: [sortedProductLines.map(productLine => /*#__PURE__*/_jsx(SuiteFeatureTableContent, {
          highlightedProducts: productsByProductLine[productLine],
          localFeatures: features[productLine],
          productLine: productLine,
          levels: levels,
          handleExpandSectionChange: handleExpandSectionChange,
          isSectionExpanded: isSectionExpanded,
          isFeatureTableExpanded: isFeatureTableExpanded,
          handleExpandFeatureTableChange: handleExpandFeatureTableChange
        }, productLine)), /*#__PURE__*/_jsx(CommerceHubFeatureContent, {
          levels: levels,
          handleExpandSectionChange: handleExpandSectionChange,
          isSectionExpanded: isSectionExpanded,
          handleExpandFeatureTableChange: handleExpandFeatureTableChange,
          isFeatureTableExpanded: isFeatureTableExpanded
        })]
      })]
    })
  });
};

export default SuiteFeatureTable;